import type { World } from '../sim/types';
import { plainTitle } from './surveyNames';
import { civilFromEpochDay, dateLabel, epochDayOf } from '../sim/calendar';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { SURVEY_NAMES, isCleanupHome, protectionJob } from '../sim/operations';
import { shoreDebrisT } from '../sim/shoreDebris';
import { managementOf } from './overview';
import { escapeHtml } from './format';

/**
 * How long a step done stays on its island's card as "Done" (M295, the one-island roadmap's R5): a month, a game choice
 * (M178), the month's report taking it up after.
 */
export const DONE_DAYS = 30;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

/** A survey's next step, its findings (M313: on the done card, the link itself). */
const FINDINGS_NEXT = 'Read its findings: what was counted against the count before.';

/** A step done on an island: its kind, its day, what was done and what it enables next. */
export interface Milestone {
  kind: 'patrol' | 'volunteers' | 'consulted' | 'rule' | 'survey';
  day: number;
  done: string;
  next: string;
}

/**
 * The operational steps done on an island, each from what the operations already hold (no event, no rate, nothing new in
 * the world): a patrol deployed (its day from the command's own result in the HUD, `patrolStarted`, since the world keeps
 * no patrol's first day), its volunteers trained (`community`, the day they count from), its protected area's consultation
 * ended and its rule made (the rule's job and the finished rule's), and a survey home from it (`finished`). Newest first,
 * those within `DONE_DAYS` of today.
 */
export function milestonesOn(world: World, island: ReefIsland, patrolStarted?: number): Milestone[] {
  const ops = world.operations;
  const today = epochDayOf(world);
  const name = ISLAND_NAMES[island];
  const x = managementOf(world)?.islands.find((y) => y.island === island);
  const out: Milestone[] = [];
  const pct = (v: number | undefined): string => `${Math.round(100 * (v ?? 0))}%`;
  if (x && x.patrol.level > 0 && patrolStarted !== undefined) {
    out.push({ kind: 'patrol', day: patrolStarted, done: `Patrol deployed on ${name}: the forbidden catch taken falls from ${pct(x.patrol.takenAnyway[0])} to ${pct(x.patrol.takenAnyway[x.patrol.level])}, from the day it began.`,
      next: `${x.patrol.until !== null ? `Its term ends ${dateLabel(x.patrol.until)}. ` : ''}The fish answer over years, and a count shows it.` });
  }
  const since = ops.community[REEF_ISLANDS.indexOf(island)];
  if (since !== null && since !== undefined) {
    // Counted at a month's end after the day they are trained (`stepOperations`: a month's last day trained counts the next).
    const m = civilFromEpochDay(since + 1);
    out.push({ kind: 'volunteers', day: since, done: `Volunteers trained on ${name}: monthly fish counts begin.`, next: `Their first count at the end of ${MONTHS[m.month - 1]}.` });
  }
  const job = protectionJob(ops, island);
  if (job && today >= (job.phaseEnds[0] as number)) {
    out.push({ kind: 'consulted', day: job.phaseEnds[0] as number, done: `Consultation on ${name} ended: the proposal goes to the State.`, next: `The rule, about ${dateLabel(job.phaseEnds[1] as number)}; nothing changes on the water until then.` });
  }
  const made = ops.finished.filter((f) => f.kind === 'rule' && f.title === `A Herbivore Protection Area on ${name}: in force`).at(-1);
  if (made && x?.herbivoresProtected) {
    out.push({ kind: 'rule', day: made.endDay, done: `A Herbivore Protection Area is in force on ${name}: no parrotfish, kala, nenue or manini may be taken.`, next: 'Patrols keep it. The fish answer over a few years, the coral over many.' });
  }
  const home = ops.finished.filter((f) => f.kind === 'survey' && f.island === island).at(-1);
  if (home) {
    const what = plainTitle(Object.values(SURVEY_NAMES).find((n) => home.title.startsWith(n)) ?? 'A survey');
    // A shore cleanup told as a survey home is (M307), with what it removed and what lies there now.
    out.push(isCleanupHome(home)
      ? { kind: 'survey', day: home.endDay, done: `${what} home from ${name}: ${(home.tonnes ?? 0).toFixed(1)} t of nets and debris removed.`, next: `About ${Math.round(shoreDebrisT(world, island))} t still lies on its shore.` }
      // Its findings read since it came home (`readThreats`'s latest day, M319), its next step the next count.
      : { kind: 'survey', day: home.endDay, done: `${what} home from ${name}.`, next: (world.campaign.marks.readThreats ?? -Infinity) >= home.endDay ? 'Findings read: the next count tells which way it goes.' : FINDINGS_NEXT });
  }
  return out.filter((d) => d.day <= today && today - d.day < DONE_DAYS).sort((a, b) => b.day - a.day);
}

/** The newest step done on an island, as its card's "Done" line; none where nothing is within `DONE_DAYS`. */
export function doneCardHtml(world: World, island: ReefIsland, patrolStarted?: number): string {
  const d = milestonesOn(world, island, patrolStarted)[0];
  if (!d) return '';
  // A survey home: its next step the findings' own link, not told again beside it (M313: the island page's words, to make
  // room for the fourth card); a cleanup's, what still lies there, and its link.
  const findings = '<button class="link" data-act="open" data-kind="findings">';
  const next = d.next === FINDINGS_NEXT ? `Next: ${findings}Read its findings</button>` : `Next: ${escapeHtml(d.next)}`;
  return `<div class="done-card" data-kind="${d.kind}"><p><strong>Done:</strong> ${escapeHtml(d.done)}</p><p class="row-note">${next}</p>${d.kind === 'survey' && d.next !== FINDINGS_NEXT ? `${findings}The findings</button>` : ''}</div>`;
}
